import { createApi } from "@reduxjs/toolkit/query/react";
import { useDispatch } from "react-redux";

import {
    LockboxAccessResponse,
    LockboxAccessResponseApi,
    LockboxDetail,
    LockboxDetailApi,
    LockboxSummary,
    LockboxSummaryApi,
    parseLockboxAccessFromApi,
    parseLockboxDetailFromApi,
    parseLockboxSummaryListFromApi
} from "../types";
import { abfSerializeQueryArgs, marketsBaseQuery } from "./util";

const LOCKBOX_TAG = "Lockbox";
const LOCKBOX_ACCESS_TAG = "LockboxAccess";

const ROUTE_LOCKBOX_ACCESS = "/lockbox/access";
const ROUTE_LOCKBOX_BY_WALLET = "/lockbox/by-wallet";

export const lockboxApi = createApi({
    reducerPath: "lockboxApi",
    tagTypes: [LOCKBOX_TAG, LOCKBOX_ACCESS_TAG],
    baseQuery: marketsBaseQuery,
    serializeQueryArgs: abfSerializeQueryArgs,
    endpoints: (builder) => ({
        lockboxAccess: builder.query<LockboxAccessResponse, void>({
            providesTags: [LOCKBOX_ACCESS_TAG],
            query: () => ({ url: ROUTE_LOCKBOX_ACCESS, method: "GET" }),
            transformResponse: (res: LockboxAccessResponseApi) => parseLockboxAccessFromApi(res)
        }),
        lockboxesByWallet: builder.query<LockboxSummary[], { wallet: string }>({
            providesTags: [LOCKBOX_TAG],
            query: ({ wallet }) => ({
                url: ROUTE_LOCKBOX_BY_WALLET,
                method: "GET",
                params: { wallet }
            }),
            transformResponse: (res: LockboxSummaryApi[]) => parseLockboxSummaryListFromApi(res)
        }),
        lockbox: builder.query<LockboxDetail, { address: string }>({
            providesTags: [LOCKBOX_TAG],
            query: ({ address }) => ({
                url: `/lockbox/${address}`,
                method: "GET"
            }),
            transformResponse: (res: LockboxDetailApi) => parseLockboxDetailFromApi(res)
        }),
        lockboxByNftMint: builder.query<LockboxDetail, { mint: string }>({
            providesTags: [LOCKBOX_TAG],
            query: ({ mint }) => ({
                url: "/lockbox/by-nft-mint",
                method: "GET",
                params: { mint }
            }),
            transformResponse: (res: LockboxDetailApi) => parseLockboxDetailFromApi(res)
        })
    })
});

export const { useLockboxAccessQuery, useLockboxesByWalletQuery, useLockboxQuery, useLockboxByNftMintQuery } =
    lockboxApi;

export function useLockboxApi() {
    const dispatch = useDispatch();
    return {
        resetLockboxApi: () => dispatch(lockboxApi.util.invalidateTags([LOCKBOX_TAG])),
        resetLockboxAccessApi: () => dispatch(lockboxApi.util.invalidateTags([LOCKBOX_ACCESS_TAG]))
    };
}
